import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { map } from "rxjs/operators";
import { AppSettings } from "../../../config/config";
import { apiConstants } from "../../constant/apiConstant";
import { StudentFeeOverdueRecord, StudentFeeDetailLine } from "./staff-fees-pending-report.service";

// "Fees Overdue Report" — a separate, parallel flow to "Fee Pending
// Report" (see StaffFeesPendingReportService), wired to a different set of
// endpoints. Field names differ slightly (activeStudentTotalOverdueAmount /
// activeStudentOverdueCount here vs. ...Pending... there for the class
// list), and Screen 3's response here is simpler — no paidAmount, no
// isPaid split, no feeStatus/colorCode tag.
export interface OverdueFeeClassRow {
  classNumber: string;
  classDivision: string;
  classId: string;
  totalActiveStudentCount: number;
  activeStudentTotalOverdueAmount: number;
  activeStudentOverdueCount: number;
}

export interface GetPendingClassListResponse {
  status: boolean;
  message?: string;
  body?: {
    reportClassData: OverdueFeeClassRow[];
    lastUpdatedOn: string;
  };
}

// Response shape here reuses "Pending" field names even though this is the
// Overdue flow — the backend's own contract doesn't fully separate the two
// concepts either (per spec), so this is intentionally identical to
// GetOverdueStudentListResponse's body shape, not a copy/paste mistake.
export interface GetPendingStudentListResponse {
  status: boolean;
  message?: string;
  body?: {
    studentFeesOverdueData: StudentFeeOverdueRecord[];
    totalActiveStudentPendingCount: number;
    totalDeactiveStudentPendingCount: number;
    totalActiveStudentPendingAmount: number;
    totalPendingAmount: number;
  };
}

// No isPaid/feeStatus/colorCode/PaymentDate here — this screen only ever
// shows one unpaid/overdue breakdown, no Paid tab.
export interface OverdueFeeTermDetail {
  TermName: string | null;
  typename: string | null;
  DueDate: string;
  studentFeeDetail: StudentFeeDetailLine[];
}

export interface GetPendingStudentHistoryResponse {
  status: boolean;
  message?: string;
  body?: {
    totalAmount: number;
    pendingAmount: number;
    percentagePendingAmount: number;
    feesDetail: OverdueFeeTermDetail[];
  };
}

@Injectable({ providedIn: 'root' })
export class StaffFeesOverdueReportService {
  private url = AppSettings.API_ENDPOINT_STAFF;

  constructor(private http: HttpClient) {}

  getPendingClassList(staffId: number | string) {
    const params = new HttpParams()
      .set('staffId', String(staffId))
      .set('lang', 'en');
    return this.http
      .get<GetPendingClassListResponse>(this.url + apiConstants.getPendingClassList, { params })
      .pipe(map((res) => res));
  }

  getPendingStudentList(staffId: number | string, classId: number | string) {
    const params = new HttpParams()
      .set('staffId', String(staffId))
      .set('classId', String(classId))
      .set('lang', 'en');
    return this.http
      .get<GetPendingStudentListResponse>(this.url + apiConstants.getPendingStudentList, { params })
      .pipe(map((res) => res));
  }

  getPendingStudentHistory(staffId: number | string, classId: number | string, studentId: number | string) {
    const params = new HttpParams()
      .set('staffId', String(staffId))
      .set('classId', String(classId))
      .set('studentId', String(studentId))
      .set('lang', 'en');
    return this.http
      .get<GetPendingStudentHistoryResponse>(this.url + apiConstants.getPendingStudentListHistory, { params })
      .pipe(map((res) => res));
  }

  refreshFeesDataOverdue(staffId: number | string) {
    const body = { staffId, lang: 'en' };
    return this.http
      .post<{ status: boolean; body?: { message: string } }>(this.url + apiConstants.refreshFeesDataOverdue, body)
      .pipe(map((res) => res));
  }
}
